import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdClose, MdRefresh, MdSend, MdSearch, MdArticle,
  MdVisibility, MdAddPhotoAlternate, MdVideoLibrary,
  MdEdit, MdDelete, MdLaunch, MdCheckCircle, MdError,
  MdFormatBold, MdFormatItalic, MdFormatUnderlined,
  MdPalette, MdFormatListBulleted, MdFormatListNumbered,
  MdFormatAlignLeft, MdFormatAlignCenter, MdFormatAlignRight
} from 'react-icons/md';
import { FaFacebook, FaInstagram, FaThreads, FaXTwitter } from 'react-icons/fa6';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDateTime } from '../utils/format';

const PLATFORMS = [
  { key: 'facebook', label: 'Facebook', icon: FaFacebook, color: '#1877F2', connectable: true },
  { key: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#E4405F', connectable: true },
  { key: 'threads', label: 'Threads', icon: FaThreads, color: 'var(--text)', connectable: true },
  { key: 'x', label: 'X (Twitter)', icon: FaXTwitter, color: 'var(--text)', connectable: false },
];

function toUnicodeBold(str) {
  return str.replace(/[a-zA-Z0-9]/g, (char) => {
    const code = char.codePointAt(0);
    if (code >= 65 && code <= 90) return String.fromCodePoint(0x1d400 + (code - 65));
    if (code >= 97 && code <= 122) return String.fromCodePoint(0x1d41a + (code - 97));
    if (code >= 48 && code <= 57) return String.fromCodePoint(0x1d7ce + (code - 48));
    return char;
  });
}

function toUnicodeItalic(str) {
  return str.replace(/[a-zA-Z]/g, (char) => {
    const code = char.codePointAt(0);
    if (code >= 65 && code <= 90) return String.fromCodePoint(0x1d434 + (code - 65));
    if (code >= 97 && code <= 122) return String.fromCodePoint(0x1d44e + (code - 97));
    return char;
  });
}

function toUnicodeBoldItalic(str) {
  return str.replace(/[a-zA-Z]/g, (char) => {
    const code = char.codePointAt(0);
    if (code >= 65 && code <= 90) return String.fromCodePoint(0x1d468 + (code - 65));
    if (code >= 97 && code <= 122) return String.fromCodePoint(0x1d482 + (code - 97));
    return char;
  });
}

function htmlToPlainText(html) {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  let text = '';

  function walk(node, isBold = false, isItalic = false) {
    if (node.nodeType === Node.TEXT_NODE) {
      let val = node.nodeValue;
      if (isBold && isItalic) {
        val = toUnicodeBoldItalic(val);
      } else if (isBold) {
        val = toUnicodeBold(val);
      } else if (isItalic) {
        val = toUnicodeItalic(val);
      }
      text += val;
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.nodeName.toLowerCase();
      const isHeading = /^h[1-6]$/.test(tag);
      const nodeIsBold = isBold || tag === 'b' || tag === 'strong' || isHeading || node.style.fontWeight === 'bold' || node.style.fontWeight === '700';
      const nodeIsItalic = isItalic || tag === 'i' || tag === 'em' || node.style.fontStyle === 'italic';

      if (tag === 'br') {
        text += '\n';
        return;
      }
      const isBlock = ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'].includes(tag);
      if (isBlock && text.length > 0 && !text.endsWith('\n')) {
        text += '\n';
      }
      if (tag === 'li') {
        const parent = node.parentNode?.nodeName.toLowerCase();
        if (parent === 'ol') {
          const index = Array.from(node.parentNode.children).indexOf(node) + 1;
          text += `${index}. `;
        } else {
          text += '• ';
        }
      }
      for (let i = 0; i < node.childNodes.length; i++) {
        walk(node.childNodes[i], nodeIsBold, nodeIsItalic);
      }
      if (isBlock && !text.endsWith('\n')) {
        text += '\n';
      }
    }
  }

  for (let i = 0; i < temp.childNodes.length; i++) {
    walk(temp.childNodes[i]);
  }
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

function plainTextToHtml(text) {
  if (!text) return '';
  return text.split('\n').map(line => `<div>${line || '<br>'}</div>`).join('');
}

function RichTextEditor({ value, onChange, placeholder, style, minHeight = '100px' }) {
  const editorRef = useRef(null);
  const colorInputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCmd = (command, value = null) => {
    document.execCommand(command, false, value);
    handleInput();
  };

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    background: 'var(--card)',
    cursor: 'pointer',
    color: 'var(--text)',
    transition: 'all 0.15s ease',
  };

  const hoverIn = (e) => {
    e.currentTarget.style.background = '#F3F4F6';
    e.currentTarget.style.borderColor = '#9CA3AF';
  };

  const hoverOut = (e) => {
    e.currentTarget.style.background = '#fff';
    e.currentTarget.style.borderColor = '#D1D5DB';
  };

  return (
    <div
      style={{
        border: isFocused ? '1px solid #4F46E5' : '1px solid #E5E7EB',
        borderRadius: '8px',
        overflow: 'hidden',
        background: 'var(--card)',
        boxShadow: isFocused ? '0 0 0 3px rgba(79, 70, 229, 0.15), 0 1px 2px 0 rgba(0, 0, 0, 0.05)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease',
        ...style
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          borderBottom: '1px solid #E5E7EB',
          background: 'var(--bg)',
          flexWrap: 'wrap',
        }}
      >
        <select
          onChange={(e) => execCmd('formatBlock', e.target.value)}
          defaultValue="div"
          style={{
            padding: '4px 8px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            background: 'var(--card)',
            fontSize: '13px',
            color: 'var(--text)',
            cursor: 'pointer',
            outline: 'none',
            marginRight: '4px',
          }}
        >
          <option value="div">Paragraph</option>
          <option value="<h1>">Heading 1</option>
          <option value="<h2>">Heading 2</option>
          <option value="<h3>">Heading 3</option>
        </select>

        <div style={{ width: '1px', height: '20px', background: 'var(--bg)', margin: '0 4px' }} />

        <button
          type="button"
          onClick={() => execCmd('bold')}
          style={buttonStyle}
          onMouseOver={hoverIn}
          onMouseOut={hoverOut}
          title="Bold"
        >
          <MdFormatBold size={18} />
        </button>

        <button
          type="button"
          onClick={() => execCmd('italic')}
          style={buttonStyle}
          onMouseOver={hoverIn}
          onMouseOut={hoverOut}
          title="Italic"
        >
          <MdFormatItalic size={18} />
        </button>

        <button
          type="button"
          onClick={() => execCmd('underline')}
          style={buttonStyle}
          onMouseOver={hoverIn}
          onMouseOut={hoverOut}
          title="Underline"
        >
          <MdFormatUnderlined size={18} />
        </button>

        <button
          type="button"
          onClick={() => colorInputRef.current.click()}
          style={buttonStyle}
          onMouseOver={hoverIn}
          onMouseOut={hoverOut}
          title="Text Color"
        >
          <MdPalette size={18} />
        </button>

        <input
          ref={colorInputRef}
          type="color"
          onChange={(e) => execCmd('foreColor', e.target.value)}
          style={{ display: 'none' }}
        />

        <div style={{ width: '1px', height: '20px', background: 'var(--bg)', margin: '0 4px' }} />

        <button
          type="button"
          onClick={() => execCmd('insertUnorderedList')}
          style={buttonStyle}
          onMouseOver={hoverIn}
          onMouseOut={hoverOut}
          title="Bullet List"
        >
          <MdFormatListBulleted size={18} />
        </button>

        <button
          type="button"
          onClick={() => execCmd('insertOrderedList')}
          style={buttonStyle}
          onMouseOver={hoverIn}
          onMouseOut={hoverOut}
          title="Numbered List"
        >
          <MdFormatListNumbered size={18} />
        </button>

        <div style={{ width: '1px', height: '20px', background: 'var(--bg)', margin: '0 4px' }} />

        <button
          type="button"
          onClick={() => execCmd('justifyLeft')}
          style={buttonStyle}
          onMouseOver={hoverIn}
          onMouseOut={hoverOut}
          title="Align Left"
        >
          <MdFormatAlignLeft size={18} />
        </button>

        <button
          type="button"
          onClick={() => execCmd('justifyCenter')}
          style={buttonStyle}
          onMouseOver={hoverIn}
          onMouseOut={hoverOut}
          title="Align Center"
        >
          <MdFormatAlignCenter size={18} />
        </button>

        <button
          type="button"
          onClick={() => execCmd('justifyRight')}
          style={buttonStyle}
          onMouseOver={hoverIn}
          onMouseOut={hoverOut}
          title="Align Right"
        >
          <MdFormatAlignRight size={18} />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          padding: '12px 14px',
          minHeight: minHeight,
          outline: 'none',
          fontSize: '14px',
          color: 'var(--text)',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap',
        }}
        placeholder={placeholder}
      />
    </div>
  );
}

const emptyCompose = {
  article_id: null,
  title: '',
  message: '',
  titleHtml: '',
  messageHtml: '',
  image_urls: [],
  video_url: '',
  thumbnail_url: '',
  selected_account_ids: [],
};

export default function SocialMedia() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [connecting, setConnecting] = useState(null); // platform key mid-connect

  const [posts, setPosts] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loadingPosts, setLoadingPosts] = useState(true);

  const [compose, setCompose] = useState(emptyCompose);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [posting, setPosting] = useState(false);

  const [articleSearch, setArticleSearch] = useState('');
  const [articleResults, setArticleResults] = useState([]);
  const [articleDropdownOpen, setArticleDropdownOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const [sendToReview, setSendToReview] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const [editingGroup, setEditingGroup] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', message: '', titleHtml: '', messageHtml: '', image_urls: [], video_url: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const [deletingGroup, setDeletingGroup] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openEditModal = (group) => {
    setEditingGroup(group);
    setEditForm({
      title: group.title || '',
      message: group.message || '',
      titleHtml: plainTextToHtml(group.title),
      messageHtml: plainTextToHtml(group.message),
      image_urls: group.image_urls || [],
      video_url: group.video_url || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingGroup) return;
    setSavingEdit(true);
    const plainTitle = htmlToPlainText(editForm.titleHtml);
    const plainMessage = htmlToPlainText(editForm.messageHtml);
    try {
      const res = await api.put(`/admin/social/posts/group/${editingGroup.group_id}`, {
        title: plainTitle.trim() || null,
        message: plainMessage.trim() || null,
        image_urls: editForm.image_urls,
        video_url: editForm.video_url || null,
      });

      toast.success('Social post group updated');

      if (res.data.update_results) {
        const warnings = res.data.update_results.filter((r) => r.status === 'warning');
        if (warnings.length > 0) {
          warnings.forEach((w) => toast.error(`${w.platform.toUpperCase()}: ${w.message}`));
        }
      }

      setEditingGroup(null);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update post group');
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeleteModal = (group) => {
    setDeletingGroup(group);
  };

  const handleConfirmDelete = async () => {
    if (!deletingGroup) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/social/posts/group/${deletingGroup.group_id}`);
      toast.success('Social media post group deleted');
      setDeletingGroup(null);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete post group');
    } finally {
      setDeleting(false);
    }
  };

  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const res = await api.get('/admin/social/accounts');
      setAccounts(res.data.data || []);
    } catch {
      toast.error('Failed to load connected accounts');
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const res = await api.get('/admin/social/posts', { params: { page, limit } });
      setPosts(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      toast.error('Failed to load logs');
    } finally {
      setLoadingPosts(false);
    }
  }, [page, limit]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  useEffect(() => {
    if (searchParams.get('connected')) {
      toast.success('Account connected');
      fetchAccounts();
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('connected');
        return next;
      }, { replace: true });
    } else if (searchParams.get('connect_error')) {
      toast.error(`Connect failed: ${searchParams.get('connect_error')}`);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('connect_error');
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Ctrl+V paste for images copied to clipboard (screenshots, copied image files, web images)
  useEffect(() => {
    const handlePaste = async (e) => {
      const clipboardItems = e.clipboardData?.items;
      if (!clipboardItems) return;

      const imageFiles = [];
      for (let i = 0; i < clipboardItems.length; i++) {
        const item = clipboardItems[i];
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length === 0) return;

      e.preventDefault();
      setUploadingImage(true);
      const toastId = toast.loading(`Uploading ${imageFiles.length} pasted image(s)...`);

      try {
        const newUrls = [];
        for (const file of imageFiles) {
          const fd = new FormData();
          fd.append('file', file);
          const res = await api.post('/admin/upload/image', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (res.data.data?.url) newUrls.push(res.data.data.url);
        }
        setCompose((c) => ({ ...c, image_urls: [...c.image_urls, ...newUrls] }));
        toast.success('Pasted image(s) added', { id: toastId });
      } catch {
        toast.error('Failed to upload pasted image', { id: toastId });
      } finally {
        setUploadingImage(false);
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const connect = async (platformKey) => {
    setConnecting(platformKey);
    try {
      const res = await api.get(`/admin/social/${platformKey}/connect`);
      window.location.href = res.data.data.url;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start connect');
      setConnecting(null);
    }
  };

  const disconnect = async (account) => {
    if (!window.confirm(`Disconnect ${account.account_name || account.platform}?`)) return;
    try {
      await api.delete(`/admin/social/accounts/${account.id}`);
      toast.success('Disconnected');
      fetchAccounts();
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  // Upload single or multiple images
  const uploadImages = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      setUploadingImage(true);
      try {
        const newUrls = [];
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          const res = await api.post('/admin/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          if (res.data.data?.url) newUrls.push(res.data.data.url);
        }
        setCompose((c) => ({ ...c, image_urls: [...c.image_urls, ...newUrls] }));
        toast.success(`${newUrls.length} image(s) uploaded`);
      } catch {
        toast.error('Image upload failed');
      } finally {
        setUploadingImage(false);
      }
    };
    input.click();
  };

  const removeImage = (index) => {
    setCompose((c) => ({
      ...c,
      image_urls: c.image_urls.filter((_, i) => i !== index),
    }));
  };

  // Extract video frame on client as instant backup
  const extractClientVideoFrame = (file) => {
    return new Promise((resolve) => {
      try {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        const objectUrl = URL.createObjectURL(file);
        video.src = objectUrl;
        video.onloadeddata = () => {
          video.currentTime = Math.min(1, (video.duration && video.duration > 1) ? 1 : 0.1);
        };
        video.onseeked = () => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 720;
          canvas.height = video.videoHeight || 405;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
              URL.revokeObjectURL(objectUrl);
              resolve(blob);
            }, 'image/jpeg', 0.85);
          } else {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
          }
        };
        video.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(null);
        };
      } catch {
        resolve(null);
      }
    });
  };

  // Upload video & auto-extract thumbnail
  const uploadVideo = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingVideo(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/admin/upload/video', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const videoUrl = res.data.data?.url || '';
        let thumbnailUrl = res.data.data?.thumbnail_url || '';

        // Fallback: If backend frame extraction didn't produce a thumbnail, extract client-side and upload
        if (!thumbnailUrl) {
          try {
            const frameBlob = await extractClientVideoFrame(file);
            if (frameBlob) {
              const frameFd = new FormData();
              frameFd.append('file', frameBlob, `thumb-${Date.now()}.jpg`);
              const thumbRes = await api.post('/admin/upload/image', frameFd, { headers: { 'Content-Type': 'multipart/form-data' } });
              thumbnailUrl = thumbRes.data.data?.url || '';
            }
          } catch {
            /* ignore fallback */
          }
        }

        setCompose((c) => ({
          ...c,
          video_url: videoUrl,
          thumbnail_url: thumbnailUrl,
        }));
        toast.success(thumbnailUrl ? 'Video uploaded & thumbnail extracted' : 'Video uploaded');
      } catch {
        toast.error('Video upload failed');
      } finally {
        setUploadingVideo(false);
      }
    };
    input.click();
  };

  const uploadCustomThumbnail = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingImage(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/admin/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const newUrl = res.data.data?.url;
        if (newUrl) {
          setCompose((c) => ({ ...c, thumbnail_url: newUrl }));
          toast.success('Custom thumbnail updated');
        }
      } catch {
        toast.error('Thumbnail upload failed');
      } finally {
        setUploadingImage(false);
      }
    };
    input.click();
  };

  const removeVideo = () => {
    setCompose((c) => ({ ...c, video_url: '', thumbnail_url: '' }));
  };

  // Debounced article search
  useEffect(() => {
    if (!articleSearch.trim()) { setArticleResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/admin/articles', { params: { status: 'published', search: articleSearch.trim(), limit: 8 } });
        setArticleResults(res.data.data || []);
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [articleSearch]);

  const pickArticle = (article) => {
    setSelectedArticle(article);
    setCompose((c) => ({
      ...c,
      article_id: article.id,
      title: c.title || article.title || '',
      message: c.message || article.summary || '',
      titleHtml: c.titleHtml || (article.title ? `<div>${article.title}</div>` : ''),
      messageHtml: c.messageHtml || (article.summary ? `<div>${article.summary}</div>` : ''),
      image_urls: c.image_urls.length > 0 ? c.image_urls : (article.image_url ? [article.image_url] : []),
      video_url: c.video_url || article.video_url || '',
      thumbnail_url: c.thumbnail_url || article.thumbnail_url || article.image_url || '',
    }));
    setArticleDropdownOpen(false);
    setArticleSearch('');
  };

  const clearArticle = () => {
    setSelectedArticle(null);
    setCompose((c) => ({ ...c, article_id: null }));
  };

  const toggleAccount = (accountId) => {
    setCompose((c) => ({
      ...c,
      selected_account_ids: c.selected_account_ids.includes(accountId)
        ? c.selected_account_ids.filter((id) => id !== accountId)
        : [...c.selected_account_ids, accountId],
    }));
  };

  const validateCompose = () => {
    if (compose.selected_account_ids.length === 0) {
      toast.error('Select at least one connected account');
      return false;
    }
    const plainTitle = htmlToPlainText(compose.titleHtml);
    const plainMessage = htmlToPlainText(compose.messageHtml);
    if (!plainTitle.trim() && !plainMessage.trim() && compose.image_urls.length === 0 && !compose.video_url) {
      toast.error('Add a title, message, or media attachment');
      return false;
    }
    return true;
  };

  const openPreview = () => {
    if (validateCompose()) {
      setPreviewModalOpen(true);
    }
  };

  const postNow = async () => {
    if (!validateCompose()) return;

    setPosting(true);
    const plainTitle = htmlToPlainText(compose.titleHtml);
    const plainMessage = htmlToPlainText(compose.messageHtml);
    const primaryThumb = compose.thumbnail_url || (compose.image_urls.length > 0 ? compose.image_urls[0] : null);
    try {
      await api.post('/admin/social/posts', {
        title: plainTitle.trim() || null,
        article_id: compose.article_id,
        message: plainMessage.trim() || null,
        image_url: compose.image_urls[0] || primaryThumb || null,
        image_urls: compose.image_urls,
        video_url: compose.video_url || null,
        thumbnail_url: primaryThumb,
        social_account_ids: compose.selected_account_ids,
        send_to_review: sendToReview,
      });
      toast.success(sendToReview ? 'Posting started & submitted to Review Article' : 'Posting started');
      setCompose(emptyCompose);
      setSelectedArticle(null);
      setSendToReview(false);
      setPreviewModalOpen(false);
      setPage(1);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const retry = async (post) => {
    try {
      await api.post(`/admin/social/posts/${post.id}/retry`);
      toast.success('Retrying…');
      setTimeout(() => fetchPosts(), 1500);
    } catch {
      toast.error('Retry failed');
    }
  };

  const connectedAccounts = accounts.filter((a) => a.status === 'connected');

  const statusBadge = (status) => {
    const styles = {
      success: { background: '#DCFCE7', color: '#166534' },
      failed: { background: '#FEE2E2', color: '#991B1B' },
      pending: { background: '#FEF3C7', color: '#92400E' },
      posting: { background: '#DBEAFE', color: '#1E40AF' },
    };
    return <span className="badge" style={styles[status] || {}}>{status}</span>;
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Social Media</h2>
        <p className="text-muted">Connect your accounts and publish articles to social platforms.</p>
      </div>

      {/* Connected Accounts */}
      <div className="card">
        <div className="section-header"><h3>Connected Accounts</h3></div>
        {loadingAccounts ? <p className="text-muted">Loading…</p> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, marginTop: 12 }}>
            {PLATFORMS.map((p) => {
              const platformAccounts = connectedAccounts.filter((a) => a.platform === p.key);
              return (
                <div key={p.key} className="card" style={{ padding: 16, background: 'var(--bg)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <p.icon size={22} color={p.color} />
                    <strong>{p.label}</strong>
                  </div>

                  {!p.connectable ? (
                    <span className="badge badge-neutral">Coming soon</span>
                  ) : platformAccounts.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {platformAccounts.map((a) => (
                        <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px' }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{a.account_name}</span>
                          <button className="btn btn-xs btn-danger" onClick={() => disconnect(a)}>Disconnect</button>
                        </div>
                      ))}
                      <button className="btn btn-xs btn-ghost" onClick={() => connect(p.key)} disabled={connecting === p.key}>
                        <MdRefresh /> Connect another
                      </button>
                    </div>
                  ) : (
                    <button className="btn btn-sm btn-primary" onClick={() => connect(p.key)} disabled={connecting === p.key}>
                      {connecting === p.key ? 'Redirecting…' : `Connect ${p.label}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Publish to Social Media Form */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="section-header"><h3>Publish to Social Media</h3></div>

        {/* 1. Base on an article (optional) */}
        <div className="form-group" style={{ position: 'relative' }}>
          <label>Base on an article (optional)</label>
          {selectedArticle ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', background: 'var(--bg)' }}>
              <MdArticle color="#4F46E5" size={18} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{selectedArticle.title}</span>
              <button className="btn btn-xs btn-ghost" onClick={clearArticle}><MdClose /></button>
            </div>
          ) : (
            <div className="search-input-wrap">
              <MdSearch className="search-icon" />
              <input
                className="search-input"
                placeholder="Search published articles to pre-fill this post…"
                value={articleSearch}
                onChange={(e) => { setArticleSearch(e.target.value); setArticleDropdownOpen(true); }}
                onFocus={() => setArticleDropdownOpen(true)}
              />
            </div>
          )}
          {articleDropdownOpen && articleResults.length > 0 && (
            <div className="select-dropdown" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10 }}>
              <div className="options-list">
                {articleResults.map((a) => (
                  <div key={a.id} className="option-item" onClick={() => pickArticle(a)}>
                    <span>{a.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 2. Title */}
        <div className="form-group">
          <label>Title</label>
          <RichTextEditor
            value={compose.titleHtml}
            onChange={(val) => setCompose((c) => ({ ...c, titleHtml: val }))}
            placeholder="Enter post title..."
            minHeight="60px"
          />
        </div>

        {/* 3. Message */}
        <div className="form-group">
          <label>Message</label>
          <RichTextEditor
            value={compose.messageHtml}
            onChange={(val) => setCompose((c) => ({ ...c, messageHtml: val }))}
            placeholder="What do you want to post?"
            minHeight="120px"
          />
        </div>

        {/* 4. Media (Multi-image & Video) */}
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>Media (Images / Video)</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#4F46E5', background: '#EEF2FF', padding: '2px 8px', borderRadius: 6, border: '1px solid #C7D2FE' }}>
              Tip: Press <strong>Ctrl+V</strong> anywhere to paste copied images directly
            </span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              {/* Image list */}
              {compose.image_urls.map((url, idx) => (
                <div key={idx} style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={url} alt={`upload-${idx}`} style={{ width: 90, height: 90, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }} />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#DC2626', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Remove image"
                  >
                    <MdClose size={12} />
                  </button>
                </div>
              ))}

              {/* Video preview */}
              {compose.video_url && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <video src={compose.video_url} style={{ width: 140, height: 90, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }} controls />
                  <button
                    type="button"
                    onClick={removeVideo}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#DC2626', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Remove video"
                  >
                    <MdClose size={12} />
                  </button>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-sm btn-ghost" onClick={uploadImages} disabled={uploadingImage}>
                  <MdAddPhotoAlternate size={18} />
                  {uploadingImage ? 'Uploading…' : 'Add Image(s)'}
                </button>
                {!compose.video_url && (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={uploadVideo} disabled={uploadingVideo}>
                    <MdVideoLibrary size={18} />
                    {uploadingVideo ? 'Uploading…' : 'Add Video'}
                  </button>
                )}
              </div>
            </div>

            {/* Video Thumbnail (App & Web) — Extracted directly from uploaded video */}
            {compose.video_url && (
              <div style={{ marginTop: 4, maxWidth: 540 }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, display: 'block' }}>
                  VIDEO THUMBNAIL (APP & WEB)
                </label>
                <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 14, background: 'var(--card)', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}>
                  {compose.thumbnail_url ? (
                    <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', background: '#000' }}>
                      <img
                        src={compose.thumbnail_url}
                        alt="Video Thumbnail"
                        style={{ width: '100%', height: 'auto', maxHeight: 220, objectFit: 'cover', display: 'block' }}
                      />
                      <div style={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          onClick={uploadCustomThumbnail}
                          className="btn btn-xs btn-secondary"
                          style={{ background: 'rgba(0,0,0,0.75)', color: '#fff', border: 'none', backdropFilter: 'blur(4px)', fontSize: 11 }}
                        >
                          Change Thumbnail
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                      <span>Extracting thumbnail frame from video...</span>
                      <button type="button" onClick={uploadCustomThumbnail} className="btn btn-xs btn-ghost" style={{ marginTop: 8, display: 'inline-block' }}>
                        Upload Custom Thumbnail
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 5. Post to */}
        <div className="form-group">
          <label>Post to</label>
          {connectedAccounts.length === 0 ? (
            <p className="text-muted" style={{ fontSize: 13 }}>Connect an account above first.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {connectedAccounts.map((a) => {
                const platform = PLATFORMS.find((p) => p.key === a.platform);
                return (
                  <label key={a.id} className="checkbox-label" style={{ margin: 0 }}>
                    <input type="checkbox" checked={compose.selected_account_ids.includes(a.id)} onChange={() => toggleAccount(a.id)} />
                    {platform && <platform.icon size={16} color={platform.color} style={{ marginRight: 4 }} />}
                    {a.account_name} <span className="text-muted">({platform?.label})</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons: Preview, Post Now, Send to Review Article */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={openPreview} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <MdVisibility size={18} /> Preview
          </button>
          <button className="btn btn-primary" onClick={postNow} disabled={posting} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <MdSend size={18} /> {posting ? 'Posting…' : 'Post Now'}
          </button>
          <label className="checkbox-label" style={{ margin: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={sendToReview}
              onChange={(e) => setSendToReview(e.target.checked)}
            />
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>Send to Review Article</span>
          </label>
        </div>
      </div>

      {/* Preview Modal */}
      {previewModalOpen && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="card" style={{ maxWidth: 700, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 24, borderRadius: 12, background: 'var(--card)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #E5E7EB', pb: 12 }}>
              <h3 style={{ margin: 0 }}>Social Media Preview</h3>
              <button className="btn btn-xs btn-ghost" onClick={() => setPreviewModalOpen(false)}><MdClose size={20} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {compose.selected_account_ids.map((accId) => {
                const account = accounts.find((a) => a.id === accId);
                const platform = PLATFORMS.find((p) => p.key === account?.platform);
                if (!account || !platform) return null;

                return (
                  <div key={accId} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, background: '#FAFAFA' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, borderBottom: '1px solid #F3F4F6', paddingBottom: 8 }}>
                      <platform.icon size={20} color={platform.color} />
                      <strong style={{ fontSize: 14 }}>{platform.label}</strong>
                      <span className="text-muted" style={{ fontSize: 13 }}>({account.account_name})</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {/* Title */}
                      {compose.titleHtml && (
                        <div
                          style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}
                          dangerouslySetInnerHTML={{ __html: compose.titleHtml }}
                        />
                      )}

                      {/* Description / Message */}
                      {compose.messageHtml && (
                        <div
                          style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}
                          dangerouslySetInnerHTML={{ __html: compose.messageHtml }}
                        />
                      )}

                      {/* Media Grid */}
                      {compose.image_urls.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: compose.image_urls.length === 1 ? '1fr' : 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginTop: 4 }}>
                          {compose.image_urls.map((img, i) => (
                            <img key={i} src={img} alt="preview" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                          ))}
                        </div>
                      )}

                      {compose.video_url && (
                        <video src={compose.video_url} controls style={{ width: '100%', maxHeight: 240, borderRadius: 8 }} />
                      )}

                      {/* Article Badge */}
                      {selectedArticle && (
                        <div style={{ marginTop: 6, padding: '8px 12px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, fontSize: 12, color: '#1E40AF', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <MdArticle size={16} />
                          <span>Based on Article: <strong>{selectedArticle.title}</strong></span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setPreviewModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={postNow} disabled={posting} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <MdSend size={18} /> {posting ? 'Posting…' : 'Post Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingGroup && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="card" style={{ maxWidth: 650, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 24, borderRadius: 12, background: 'var(--card)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #E5E7EB', paddingBottom: 12 }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdEdit color="#4F46E5" /> Edit Social Media Post Group
              </h3>
              <button className="btn btn-xs btn-ghost" onClick={() => setEditingGroup(null)}><MdClose size={20} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-group">
                <label>Title</label>
                <RichTextEditor
                  value={editForm.titleHtml}
                  onChange={(val) => setEditForm((f) => ({ ...f, titleHtml: val }))}
                  placeholder="Enter post title..."
                  minHeight="60px"
                />
              </div>

              <div className="form-group">
                <label>Message</label>
                <RichTextEditor
                  value={editForm.messageHtml}
                  onChange={(val) => setEditForm((f) => ({ ...f, messageHtml: val }))}
                  placeholder="Update message text..."
                  minHeight="120px"
                />
              </div>

              {/* Images list */}
              {editForm.image_urls.length > 0 && (
                <div className="form-group">
                  <label>Attached Images</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {editForm.image_urls.map((img, idx) => (
                      <div key={idx} style={{ position: 'relative' }}>
                        <img src={img} alt="attached" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                        <button
                          type="button"
                          onClick={() => setEditForm((f) => ({ ...f, image_urls: f.image_urls.filter((_, i) => i !== idx) }))}
                          style={{ position: 'absolute', top: -6, right: -6, background: '#DC2626', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <MdClose size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Connected platforms notice */}
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: 12, borderRadius: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                <strong>Platforms in this group:</strong>
                <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                  {editingGroup.platforms.map((p) => {
                    const plInfo = PLATFORMS.find((pl) => pl.key === p.platform);
                    const Icon = plInfo?.icon;
                    return (
                      <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--card)', padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12 }}>
                        {Icon && <Icon size={14} color={plInfo?.color} />}
                        <span>{plInfo?.label || p.platform}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setEditingGroup(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={savingEdit}>
                {savingEdit ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingGroup && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="card" style={{ maxWidth: 500, width: '100%', padding: 24, borderRadius: 12, background: 'var(--card)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #FEE2E2', paddingBottom: 12 }}>
              <h3 style={{ margin: 0, color: '#DC2626', display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdDelete color="#DC2626" /> Delete Social Media Posts?
              </h3>
              <button className="btn btn-xs btn-ghost" onClick={() => setDeletingGroup(null)}><MdClose size={20} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: 'var(--text)' }}>
              <p>This action will delete published posts from the following social media accounts:</p>
              
              <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', padding: 12, borderRadius: 8 }}>
                {deletingGroup.platforms.map((p) => {
                  const plInfo = PLATFORMS.find((pl) => pl.key === p.platform);
                  const Icon = plInfo?.icon;
                  return (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13, fontWeight: 600, color: '#991B1B' }}>
                      {Icon && <Icon size={16} color={plInfo?.color} />}
                      <span>{plInfo?.label || p.platform} ({p.account_name})</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', padding: '8px 12px', borderRadius: 8, fontSize: 12, color: '#1E40AF', marginTop: 4 }}>
                <strong>Note:</strong> The original Udayam article will <strong>NOT</strong> be deleted.
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setDeletingGroup(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: '#DC2626', borderColor: '#DC2626', color: '#fff' }} onClick={handleConfirmDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete All Posts'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs / Reports */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="section-header"><h3>Social Media Logs</h3></div>
        {loadingPosts ? <p className="text-muted">Loading…</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>Posted On</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((group) => {
                  return (
                    <tr key={group.group_id}>
                      <td>
                        <span className="article-title" style={{ fontWeight: 600, color: 'var(--text)' }}>
                          {group.title}
                        </span>
                        {group.message && (
                          <div className="cell-sub" style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                            {group.message.length > 70 ? `${group.message.slice(0, 70)}…` : group.message}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          {group.platforms.map((p) => {
                            const plInfo = PLATFORMS.find((pl) => pl.key === p.platform);
                            const Icon = plInfo?.icon;
                            return (
                              <span
                                key={p.id}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  background: 'var(--bg)',
                                  padding: '4px 8px',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 500,
                                  color: 'var(--text)',
                                }}
                              >
                                {Icon && <Icon size={15} color={plInfo?.color} />}
                                <span>{plInfo?.label || p.platform}</span>
                                {p.status === 'success' && <MdCheckCircle color="#10B981" size={14} title="Posted successfully" />}
                                {p.status === 'failed' && <MdError color="#EF4444" size={14} title={p.error_message || 'Failed'} />}
                                {(p.status === 'pending' || p.status === 'posting') && <MdRefresh className="spin" color="#F59E0B" size={14} title="Posting..." />}
                                {p.post_url && (
                                  <a href={p.post_url} target="_blank" rel="noreferrer" title="View post on platform" style={{ color: '#4F46E5', marginLeft: 2, display: 'inline-flex', alignItems: 'center' }}>
                                    <MdLaunch size={12} />
                                  </a>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            group.failed_count > 0 && group.success_count > 0
                              ? 'badge-warning'
                              : group.failed_count > 0
                              ? 'badge-danger'
                              : 'badge-success'
                          }`}
                          style={{ fontWeight: 600, padding: '4px 10px', borderRadius: 12 }}
                        >
                          {group.status_summary}
                        </span>
                      </td>
                      <td>{formatDateTime(group.posted_at || group.created_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button className="btn btn-sm btn-ghost" onClick={() => openEditModal(group)} title="Edit post group">
                            <MdEdit size={16} /> Edit
                          </button>
                          <button className="btn btn-sm btn-ghost" style={{ color: '#DC2626' }} onClick={() => openDeleteModal(group)} title="Delete post group">
                            <MdDelete size={16} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {posts.length === 0 && (
                  <tr><td colSpan="5" className="empty-row">No social media posts yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loadingPosts && posts.length > 0 && (
          <Pagination pagination={pagination} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
        )}
      </div>
    </div>
  );
}
